import React from 'react';
import {Form} from 'formik';
import {QRCodeSVG} from 'qrcode.react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {Constants} from '../../constants';
import {Button, ButtonSize, ButtonType, ButtonVariant} from '../button';
import {Container, ContainerPadding, ContainerSize, ContainerVariant} from '../layout';
import {Heading, HeadingVariant, MarginBottom, Text, TextColor, TextVariant} from '../typography';
import {FormControl} from '../form-control';

interface OnboardingFormValues {
  phoneNumber: string;
}

interface OnboardingFormErrors {
  phoneNumber?: string;
}

interface OnboardingFormTouched {
  phoneNumber?: boolean;
}

interface OnboardingFormComponentProps {
  className?: string;
  errors: OnboardingFormErrors;
  hasError: boolean;
  isFormValid: boolean;
  isSubmitting: boolean;
  setFieldValue: (field: string, value: any) => void;
  touched: OnboardingFormTouched;
  values: OnboardingFormValues;
}

export const OnboardingFormComponent: React.FC<OnboardingFormComponentProps> = ({
  className = '',
  errors,
  hasError,
  isFormValid,
  isSubmitting,
  setFieldValue,
  touched,
  values,
}) => {
  return (
    <Container variant={ContainerVariant.Form} padding={ContainerPadding.MD} className={className}>
      <Container variant={ContainerVariant.Centered} size={ContainerSize.MD}>
        <div className="mb-8">
          <Heading level={2} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.SM}>
            Get the app via SMS
          </Heading>
          <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
            Build your visual portfolio, get AI insights, and unlock tailored opportunities.
          </Text>
        </div>

        <Form className="space-y-6">
          <FormControl label="Mobile Number" error={touched.phoneNumber ? errors.phoneNumber : undefined} required>
            <PhoneInput
              placeholder="Enter your mobile number"
              value={values.phoneNumber}
              onChange={(value: string | undefined) => {
                setFieldValue('phoneNumber', value || '');
              }}
              defaultCountry="US"
              limitMaxLength={true}
              className={`w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none placeholder:text-gray-500 placeholder:opacity-100 ${
                hasError
                  ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
              }`}
              style={
                {
                  '--PhoneInputCountryFlag-height': '1.25em',
                  '--PhoneInputCountryFlag-width': '1.75em',
                  '--PhoneInput-color--focus': hasError ? '#ef4444' : '#3b82f6',
                } as React.CSSProperties
              }
              disabled={isSubmitting}
            />
          </FormControl>

          <Button
            type={ButtonType.SUBMIT}
            variant={ButtonVariant.Primary}
            size={ButtonSize.MD}
            fullWidth
            disabled={!isFormValid}
            loading={isSubmitting}
            loadingText="Sending...">
            Send Download Link
          </Button>
        </Form>

        <div className="my-8 flex items-center">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-sm text-gray-500">Scan QR Code</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        <div className="mt-8">
          <Heading level={2} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.SM}>
            Download from app stores
          </Heading>
          <Text variant={TextVariant.BODY} color={TextColor.MUTED} marginBottom={MarginBottom.MD}>
            Point your camera here to download instantly
          </Text>

          <div className="flex justify-center items-center p-4 inline-block">
            <QRCodeSVG value={Constants.RECCO_APP_STORE_URL} size={256} level="H" includeMargin={false} />
          </div>
        </div>
      </Container>
    </Container>
  );
};
